import { useEffect, useId, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea, TextAreaBase } from "@/components/base/textarea/textarea";
import { ResetFieldConfirmDialog } from "../safety/dialogs";
import { CONFIG_FIELD_META, type ConfigField, INHERITANCE_BADGE_FIELDS, parseStringArray } from "./config-fields";

// Shape/size/weight matches the model-type pill (`AdminRiskRow.tsx`), but these two
// states use bespoke colors the shared `Badge` palette doesn't have (UNB-2723).
const InheritanceBadge = ({ isOverridden }: { isOverridden: boolean }) =>
  isOverridden ? (
    <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500">
      Edited
    </span>
  ) : (
    <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset bg-transparent text-zinc-400 ring-zinc-700">
      From catalog
    </span>
  );

interface RiskConfigEditorProps {
  fields: ConfigField[];
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  // Merchant-level usage only (UNB-2723): when provided, `description`/`hardcoded_rules`
  // get an inheritance badge (+ Reset to default when overridden) by comparing against
  // this. Omitted entirely on the Risk Catalog's own editor — nothing to inherit there.
  catalogConfig?: Record<string, unknown>;
}

export const RiskConfigEditor = ({ fields, config, onChange, catalogConfig }: RiskConfigEditorProps) => {
  const idPrefix = useId();
  // Field awaiting confirmation for "Reset to default" (UNB-2723), or null.
  const [resetPending, setResetPending] = useState<ConfigField | null>(null);

  // Local text buffers for stringArray fields so typing newlines/spaces isn't
  // immediately eaten by the parse-on-change (mirrors SemiCustomRisks).
  const arrayKeys = fields.filter((f) => f.type === "stringArray").map((f) => f.key);
  const [arrayText, setArrayText] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const key of arrayKeys) {
      init[key] = (Array.isArray(config[key]) ? (config[key] as string[]) : []).join("\n");
    }
    return init;
  });

  // Re-sync local buffers when the external config changes (e.g. Cancel resets
  // state), but only when the parsed items genuinely differ to avoid clobbering
  // in-progress typing.
  useEffect(() => {
    setArrayText((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of arrayKeys) {
        const external = Array.isArray(config[key]) ? (config[key] as string[]) : [];
        if (JSON.stringify(external) !== JSON.stringify(parseStringArray(prev[key] ?? ""))) {
          next[key] = external.join("\n");
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  if (fields.length === 0) {
    return <p className="text-sm text-tertiary">No configuration required for this model type.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {fields.map((field) => {
        const { label, placeholder, hint } = CONFIG_FIELD_META[field.key];

        if (field.type === "stringArray") {
          return (
            <TextArea
              key={field.key}
              label={label}
              placeholder={placeholder}
              hint={hint}
              rows={4}
              value={arrayText[field.key] ?? ""}
              onChange={(value: string) => {
                setArrayText((prev) => ({ ...prev, [field.key]: value }));
                onChange({ ...config, [field.key]: parseStringArray(value) });
              }}
            />
          );
        }

        if (field.type === "number") {
          return (
            <Input
              key={field.key}
              label={label}
              placeholder={placeholder}
              hint={hint}
              type="number"
              value={config[field.key] === undefined || config[field.key] === null ? "" : String(config[field.key])}
              onChange={(value: string) => {
                // Clearing an optional field removes the key ("no override")
                // rather than saving a literal 0.
                if (value === "" && field.optional) {
                  const { [field.key]: _, ...rest } = config;
                  onChange(rest);
                  return;
                }
                onChange({ ...config, [field.key]: value === "" ? 0 : Number(value) });
              }}
            />
          );
        }

        // `string` — rendered as a textarea. `description`/`hardcoded_rules` get an
        // inheritance badge + "Reset to default" when a catalog value is available
        // to compare against (merchant-level usage only, UNB-2723). Both are nullable:
        // null/"" means "not overridden", so the pipeline (and this editor) fall back
        // to the catalog's own value — no diffing needed to tell inherited from edited.
        const showInheritance = catalogConfig !== undefined && INHERITANCE_BADGE_FIELDS.has(field.key);
        const rawValue = config[field.key];
        const isOverridden = showInheritance && typeof rawValue === "string" && rawValue !== "";
        const catalogValue = catalogConfig?.[field.key];
        // Not overridden (or the catalog's own editor, no inheritance to show): fall back to
        // the catalog value so the field never renders as an empty placeholder.
        const fallback = showInheritance ? catalogValue : rawValue;
        const value = isOverridden ? (rawValue as string) : typeof fallback === "string" ? fallback : "";
        const inputId = `${idPrefix}-${field.key}`;

        if (!showInheritance) {
          return (
            <TextArea
              key={field.key}
              label={label}
              placeholder={placeholder}
              hint={hint}
              rows={3}
              value={value}
              onChange={(newValue: string) => onChange({ ...config, [field.key]: newValue })}
            />
          );
        }

        return (
          <div key={field.key} className="group flex h-max w-full flex-col items-start justify-start gap-1.5">
            <div className="flex w-full items-center gap-1.5">
              <label
                htmlFor={inputId}
                className="flex cursor-default items-center gap-0.5 text-sm font-medium text-secondary"
              >
                {label}
              </label>
              <InheritanceBadge isOverridden={isOverridden} />
              {isOverridden && (
                <Button color="link-gray" size="sm" onClick={() => setResetPending(field)}>
                  Reset to default
                </Button>
              )}
            </div>
            <TextAreaBase
              id={inputId}
              placeholder={placeholder}
              rows={3}
              value={value}
              onChange={(e) => onChange({ ...config, [field.key]: e.target.value })}
            />
            {hint && <p className="text-sm text-tertiary">{hint}</p>}
          </div>
        );
      })}

      <ResetFieldConfirmDialog
        open={resetPending !== null}
        fieldLabel={resetPending ? CONFIG_FIELD_META[resetPending.key].label : ""}
        onCancel={() => setResetPending(null)}
        onConfirm={() => {
          if (!resetPending) return;
          // Clears the override (null), not a copy of the catalog string — so the field
          // stays genuinely inherited and keeps following future catalog edits (UNB-2723).
          onChange({ ...config, [resetPending.key]: null });
          setResetPending(null);
        }}
      />
    </div>
  );
};
